import BubbleService from "@/services/Bubble/BubbleService";
import {FormState} from "@/lib/types/upgrad/upgradForm";
import {FormState as FormCoSigner} from "@/lib/types/upgrad/upgradCoSignerForm";
import {Transform} from "@/lib/helper";
import {UpgradLoanSchedule} from "@/lib/types/calulator/upgrad";

class UpgradApplicationService extends BubbleService {
    public contractCode = ''

    constructor() {
        super();
    }

    applyApplication = async (upgradForm: FormState) => {
        try {
            this.contractCode = this.prepareContractCode(
                upgradForm.formData.step3.cosignerPhoneNumber,
                upgradForm.formData.step2.faceMatching.idData.id
            )
            const application:any = await this.createApplication(upgradForm)
            const applicationSchedule = await this.createApplicationSchedule(upgradForm,application.id)
            return {application, applicationSchedule, contractCode:this.contractCode}
        } catch (e:any) {
            console.error(e.message)
        }
    }

    applyCoSignerApplication = async (upgradForm: FormCoSigner) => {
        try{
            const data = await this.buildCreateCoSignerApplicationParams(upgradForm)
            const coSignerApplication = await this.postObjWithoutAuth('Upgrad_Application_CoSigner',data)
            const application = await this.patchObjWithoutAuth(
                'Upgrad_Application/' + upgradForm.initalData.applicationId,
                {cosignerApplicationID:coSignerApplication.id}
            )
            return {status: true}
        }catch(e:any) {
            return {status: false, message: e.message}
        }
    }

    private createApplication = async (upgradForm: FormState) => {
        const data = await this.buildCreateApplicationParams(upgradForm)
        return await this.postObjWithoutAuth('Upgrad_Application',data)
    }

    private createApplicationSchedule = async (upgradForm: FormState,applicationId: string) => {
        const data = this.buildApplicationScheduleParams(upgradForm, applicationId)
        const config = {
            headers: {"Content-type": "text/plain"}
        }
        return await this.postObjWithoutAuth('Upgrad_Schedule_Payment/bulk',data,config)
    }

    private buildCreateCoSignerApplicationParams = async (upgradForm: FormCoSigner) => {
        let fileUpload:any = {
            vssid: await this.uploadMultipleFile(upgradForm.formData.step4.vssid),
            bankAccount: await this.uploadMultipleFile(upgradForm.formData.step4.bankAccount),
            laborContract: await this.uploadMultipleFile(upgradForm.formData.step4.laborContract)
        }

        if(upgradForm.formData.step4.houseBook){
            let houseBook = await this.uploadMultipleFile(upgradForm.formData.step4.houseBook)
            fileUpload = {...fileUpload,houseBook: houseBook}
        }else if(upgradForm.formData.step4.marriageRegistration){
            let marriageRegistration = await this.uploadMultipleFile(upgradForm.formData.step4.marriageRegistration)
            fileUpload = {...fileUpload,marriageRegistration: marriageRegistration}
        }

        return {
            //Files
            ...fileUpload,
            cccd: upgradForm.formData.step2.faceMatching.idData.id
        }
    }

    private buildCreateApplicationParams = async (upgradForm: FormState) => {

        let fileUpload:any = {
            vssid: await this.uploadMultipleFile(upgradForm.formData.step4.vssid),
            bankAccount: await this.uploadMultipleFile(upgradForm.formData.step4.bankAccount),
            laborContract: await this.uploadMultipleFile(upgradForm.formData.step4.laborContract)
        }

        if(upgradForm.formData.step4.houseBook){
            let houseBook = await this.uploadMultipleFile(upgradForm.formData.step4.houseBook)
            fileUpload = {...fileUpload,houseBook: houseBook}
        }else if(upgradForm.formData.step4.marriageRegistration){
            let marriageRegistration = await this.uploadMultipleFile(upgradForm.formData.step4.marriageRegistration)
            fileUpload = {...fileUpload,marriageRegistration: marriageRegistration}
        }

        return {
            //Info
            name: upgradForm.formData.step2.faceMatching.idData.name,
            email: upgradForm.formData.step1.email,
            cccd: upgradForm.formData.step2.faceMatching.idData.id,
            contractCode: this.contractCode,

            //Amount
            baseAmount: upgradForm.loanData.loanOverview.baseAmount,
            loanAmount: upgradForm.loanData.loanOverview.loanAmount,
            remainAmount: upgradForm.loanData.loanOverview.loanAmount,
            interestAmount: upgradForm.loanData.loanSchedule.sumRateAmount,
            paymentPerMonthAmount: upgradForm.loanData.loanSchedule.paymentPerMonthAmount,
            prepayAmount: upgradForm.loanData.loanOverview.prepayAmount,
            prepayAdditionalAmount: upgradForm.loanData.loanOverview.prepayAdditionalAmount,

            //Rates and exchange
            interestRate: upgradForm.loanData.loanOverview.loanRate,
            serviceRate: upgradForm.loanData.loanOverview.serviceRate,
            prepayRate: upgradForm.loanData.loanOverview.prepayRate,
            exchangeCurrency: upgradForm.loanData.exchangeCurrency.Sell,

            //Course
            courseType: upgradForm.formData.step1.courseType,
            courseFee: upgradForm.loanData.course.on_campus_fee,
            courseCurrency: upgradForm.loanData.course.on_campus_currency,
            university: upgradForm.formData.step1.university,

            //Duration
            duration: upgradForm.formData.step1.duration,
            delayDuration: upgradForm.formData.step1.delayDuration,

            //Co-Signer
            cosignerName: upgradForm.formData.step3.cosignerName,
            cosignerEmail: upgradForm.formData.step3.cosignerEmail,
            cosignerRelationship: upgradForm.formData.step3.cosignerRelationship,
            cosignerPhoneNumber: upgradForm.formData.step3.cosignerPhoneNumber,

            //Files
            ...fileUpload
        }
    }

    private buildApplicationScheduleParams = (upgradForm: FormState,applicationId: string) => {
        return upgradForm.loanData.loanSchedule.loanSchedule.map((item,index)=>{
            const schedule = {
                application: applicationId,
                code: `${applicationId}_${item.currentMonth}`,
                downBaseAmount: item.downBaseAmount,
                downDebtRemainAmount: item.downDebtRemainAmount,
                downRateAmount: item.downRateAmount,
                paymentAmount: item.paymentAmount,
                interestAmount: upgradForm.loanData.loanSchedule.paymentPerMonthWithoutInterest,
                baseAmount: item.paymentAmount - upgradForm.loanData.loanSchedule.paymentPerMonthWithoutInterest
            }
            return JSON.stringify(schedule)
        }).join('\n')
    }

    prepareContractCode = (phoneNumber: any, idCard: any) => {
        let xx = this.getLastCharacterOfString(phoneNumber, 2);
        let zz = this.getLastCharacterOfString(idCard, 2);
        let yyyy = this.generateString(4);
        let result = xx + yyyy.trim() + zz;
        return result.toUpperCase();
    };

    getLastCharacterOfString = (string: string, length: number) => {
        console.log(string)
        let lastChars = string.substring(string.length - length);
        return lastChars;
    };

    generateString(length: number) {
        const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        let result = " ";
        const charactersLength = characters.length;
        for (let i = 0; i < length; i++) {
            result += characters.charAt(
                Math.floor(Math.random() * charactersLength)
            );
        }
        return result;
    }
}

export default UpgradApplicationService;